import React, { useEffect, useEffectEvent } from 'react'

import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { useApolloQueryErrorHandling } from '@sputnik/apollo/useApolloQueryErrorHandling'
import { logInfo } from '@sputnik/utils/loggerUtils'

import { useFileUploadStatus } from './FileUploadStatusProvider'
import {
  GetCreditApplicationSupportingDocuments,
  GetCreditApplicationSupportingDocuments_creditApplication_CreditApplication,
  GetCreditApplicationSupportingDocumentsDocument,
  GetCreditApplicationSupportingDocumentsVariables,
  useGetCreditApplicationSupportingDocuments,
} from './GetCreditApplicationSupportingDocuments.gql'

export const useFileUpload = (uuid: string) => {
  const { getFileUpload, updateFileUploadState, removeFileUpload } = useFileUploadStatus()
  const showToast = useToast()

  const fileUpload = getFileUpload(uuid)

  const { state, salesforceCreditId, fileName, documentType } = fileUpload ?? {}

  const { data, error, client } = useGetCreditApplicationSupportingDocuments({
    variables: {
      creditApplicationId: salesforceCreditId ?? '',
    },
    pollInterval: 1000,
    fetchPolicy: 'no-cache',
    skip: state !== 'loading' || !salesforceCreditId,
  })

  useApolloQueryErrorHandling({
    error,
    toast: {
      message: (
        <FormattedMessage
          defaultMessage='Failed to fetch updated file upload status for {fileName}'
          id='sputnik.FileUploadItem__2TKHVK'
          values={{ fileName }}
        />
      ),
    },
    customAction: () => {
      updateFileUploadState(uuid, 'failure')
    },
  })

  const onNewFileSuccessfullyUploaded = useEffectEvent(
    (updatedCreditApplication: GetCreditApplicationSupportingDocuments_creditApplication_CreditApplication) => {
      logInfo('uploadToS3', {
        attributes: {
          creditAppId: salesforceCreditId,
          result: `Document uploaded to S3: ${documentType}`,
        },
        eventName: 'UploadDocument',
      })
      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Document {fileName} uploaded'
          id='sputnik.FileUploadSection__qc2lSL'
          values={{ fileName: fileName }}
        />,
        { autoHideDelay: 3000 },
      )
      client.cache.updateQuery<
        GetCreditApplicationSupportingDocuments,
        GetCreditApplicationSupportingDocumentsVariables
      >(
        {
          query: GetCreditApplicationSupportingDocumentsDocument,
          variables: {
            creditApplicationId: salesforceCreditId ?? '',
          },
        },
        (prev) => {
          if (!prev?.creditApplication) {
            return
          }

          return {
            ...prev,
            creditApplication: {
              ...prev.creditApplication,
              ...updatedCreditApplication,
            },
          }
        },
      )

      removeFileUpload(uuid)
    },
  )

  useEffect(() => {
    if (!data?.creditApplication?.supportingDocuments?.length || state !== 'loading') {
      return
    }

    if (
      !data.creditApplication.supportingDocuments.some(
        ({ documentUuid, fileName }) => documentUuid === uuid && !!fileName,
      )
    ) {
      return
    }

    if (
      data.creditApplication?.applicationActionItems?.some(
        ({ action, completedAt, metadata }) =>
          action === 'provide_documents' &&
          !completedAt &&
          metadata?.documentsRemaining?.some(
            (requiredDocType) => requiredDocType === `REQUESTED_DOCUMENT_${documentType}`,
          ),
      )
    ) {
      return
    }

    onNewFileSuccessfullyUploaded(data.creditApplication)
  }, [data?.creditApplication, state, documentType, uuid])

  return {
    state,
  }
}
